import { useCallback, useEffect, useState } from "react";
import { POSITION_KIND_LABEL, positionKindsApi, type PositionKindIconName, type PositionKindView } from "../api/structure";
import type { ApiError } from "../api/client";

/**
 * Position kinds (`GET /position-kinds`) shared by every screen that renders or
 * picks a kind — the structure tree, template trees, the position form. Same
 * shape as `useLocationKinds`: fetched once per session, cached at module
 * level; the admin page calls `refresh()` after a write so open pickers see it.
 */

let cache: PositionKindView[] | null = null;
let inflight: Promise<PositionKindView[]> | null = null;
const listeners = new Set<() => void>();

function sortKinds(list: PositionKindView[]): PositionKindView[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "sr-Latn"));
}

function notify() {
  for (const l of listeners) l();
}

function load(force = false): Promise<PositionKindView[]> {
  if (!force && cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = positionKindsApi
    .list()
    .then((list) => {
      cache = sortKinds(list);
      inflight = null;
      notify();
      return cache;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** Re-fetch the catalogue (after an admin write). Every mounted hook re-renders. */
export function refreshPositionKinds(): Promise<PositionKindView[]> {
  return load(true);
}

/** Test hook: drop the module cache so the next mount fetches again. */
export function resetPositionKindsCache(): void {
  cache = null;
  inflight = null;
}

/**
 * Default kind for a new child of a `parentKind` parent: the next kind in
 * display order (Pogon under Lokacija, Linija under Pogon, …), the last one
 * when the parent already has the last kind, the first one on the first level
 * or when the parent's kind is unknown.
 */
export function defaultChildKind(kinds: PositionKindView[], parentKind: string | null): string {
  if (kinds.length === 0) return "";
  if (parentKind === null) return kinds[0].code;
  const i = kinds.findIndex((k) => k.code === parentKind);
  if (i < 0) return kinds[0].code;
  return kinds[Math.min(i + 1, kinds.length - 1)].code;
}

export type PositionKindsHook = {
  /** Sorted by `sortOrder`, then name. Empty while loading or on error. */
  kinds: PositionKindView[];
  loading: boolean;
  error: string | null;
  /** Display name for a code; the seeded fallback label or the code itself when unknown. */
  labelOf: (code: string) => string;
  /** Admin-selected icon; a box when unknown. */
  iconOf: (code: string) => PositionKindIconName;
  /** Admin-selected dot colour (`#RRGGBB`); null when none or unknown. */
  colorOf: (code: string) => string | null;
  /**
   * Whether positions of this kind are slots for one asset (expected type,
   * mount, empty = red) rather than structural nodes; true when unknown so a
   * stale or not-yet-loaded codebook never hides the mount/expected-type UI.
   */
  holdsAssetOf: (code: string) => boolean;
  refresh: () => Promise<PositionKindView[]>;
};

export function usePositionKinds(): PositionKindsHook {
  const [kinds, setKinds] = useState<PositionKindView[]>(cache ?? []);
  const [loading, setLoading] = useState(cache === null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const onChange = () => {
      if (mounted && cache) setKinds(cache);
    };
    listeners.add(onChange);
    if (cache) {
      setKinds(cache);
      setLoading(false);
    } else {
      setLoading(true);
      load()
        .then((list) => { if (mounted) { setKinds(list); setError(null); } })
        .catch((err: ApiError) => { if (mounted) setError(err.message); })
        .finally(() => { if (mounted) setLoading(false); });
    }
    return () => {
      mounted = false;
      listeners.delete(onChange);
    };
  }, []);

  const labelOf = useCallback(
    (code: string) => kinds.find((k) => k.code === code)?.name ?? POSITION_KIND_LABEL[code] ?? code,
    [kinds],
  );
  const iconOf = useCallback(
    (code: string): PositionKindIconName => kinds.find((k) => k.code === code)?.icon ?? "box",
    [kinds],
  );
  const colorOf = useCallback(
    (code: string): string | null => kinds.find((k) => k.code === code)?.color ?? null,
    [kinds],
  );
  const holdsAssetOf = useCallback(
    (code: string): boolean => kinds.find((k) => k.code === code)?.holdsAsset ?? true,
    [kinds],
  );
  const refresh = useCallback(() => refreshPositionKinds(), []);

  return { kinds, loading, error, labelOf, iconOf, colorOf, holdsAssetOf, refresh };
}
